import { Controller } from '@hotwired/stimulus'

// Validación + feedback visual del submit del formulario de Pantalla 1
// (ENH-RNK-025 A-17 / A-20 / mejoras Naiara 2026-05-13). Responsabilidades:
//
// 1. Validar que cada titular declarado en el stepper ha subido los 4
//    documentos obligatorios (DNI, nómina, IRPF, vida laboral) y que el
//    consentimiento está marcado. Si no, aborta el submit y muestra:
//      - Banner consolidado arriba del consent con lista por titular:
//          "Titular 1: DNI, Nóminas"
//          "Titular 2: Vida Laboral"
//          "Aceptar la autorización para compartir documentación"
//      - Estado `AccItem-error` (borde rojo + fondo header rosa-claro) en
//        cada acordeón con doc faltante para el titular activo.
//      - Texto inline `AccErrorMsg` debajo del item: "Debes subir este
//        documento para continuar".
//      - Salta al primer titular incompleto y abre sus acordeones rojos.
//    El estado de errores persiste por titular: al cambiar de tab se
//    re-pinta el error en el otro titular si seguía incompleto. Cuando el
//    usuario sube un archivo en un acordeón con error, su estado de error
//    se limpia para ese titular.
// 2. Si la validación pasa: deshabilita el botón, cambia su texto al
//    loadingLabelValue y muestra el overlay con backdrop blur. Subir
//    20-30 PDFs por la red puede llevar varios segundos.
//
// No usamos `data-disable-with` (Rails UJS legacy) porque solo
// deshabilita pero no informa de progreso ni valida antes del submit.
//
// Targets:
//   - boton: el input[type=submit] del form
//   - overlay: div con backdrop + spinner + texto, oculto por defecto
//   - banner: <div> donde se renderiza el resumen de errores
//   - consentimiento: el checkbox de consentimiento de cesión
//
// Values:
//   - loadingLabel: texto que reemplaza al "Enviar documentación" mientras envía
//   - tiposObligatorios: array de tipos requeridos por titular (JSON)
//   - tiposLabels: objeto { tipo: "Etiqueta legible" } para el banner
//   - bannerTitulo: "Para enviar tu documentación necesitas:"
//   - documentoFaltante: "Debes subir este documento para continuar"
//   - consentimientoFaltante: "Aceptar la autorización para compartir documentación"
//   - titularPrefijo: "Titular %{n}: " — prefijo en lista cuando hay 2-3 titulares
export default class extends Controller {
  static targets = ['boton', 'overlay', 'banner', 'consentimiento']
  static values = {
    loadingLabel: String,
    // Modo "titulares" (Pantalla 1) vs "archivo único" (Pantalla 3 firma LOPD).
    // El modo se autodetecta: si el form contiene acordeones por tipo
    // (`.rnk-HipotecasDoc_AccItem`) → titulares; si no → archivo único.
    tiposObligatorios: { type: Array, default: ['dni', 'nomina', 'irpf', 'vida_laboral'] },
    tiposLabels: { type: Object, default: {} },
    bannerTitulo: { type: String, default: 'Para enviar tu documentación necesitas:' },
    documentoFaltante: { type: String, default: 'Debes subir este documento para continuar' },
    archivoFaltante: { type: String, default: 'Debes subir el archivo para continuar' },
    consentimientoFaltante: { type: String, default: 'Aceptar la autorización para compartir documentación' },
    titularPrefijo: { type: String, default: 'Titular %{n}: ' }
  }

  connect () {
    // Estado de errores persistente: { 1: ['dni', 'nomina'], 3: ['irpf'] }
    this.errorsByTitular = {}

    // Re-aplicar errores al cambiar de tab (un T2 con docs faltantes que
    // dejamos para volver a T1 debe seguir mostrándose en rojo al volver).
    this.handleTabChangeBound = this.handleTabChange.bind(this)
    this.handleFileChangeBound = this.handleFileChange.bind(this)
    const root = this.elementoRaiz()
    root.addEventListener('hipotecas-doc-titulares:tab-changed', this.handleTabChangeBound)
    root.addEventListener('change', this.handleFileChangeBound)
  }

  disconnect () {
    const root = this.elementoRaiz()
    if (this.handleTabChangeBound) root.removeEventListener('hipotecas-doc-titulares:tab-changed', this.handleTabChangeBound)
    if (this.handleFileChangeBound) root.removeEventListener('change', this.handleFileChangeBound)
  }

  enviando (event) {
    const validacion = this.validarFormulario()
    if (validacion.hasErrors) {
      event.preventDefault()
      this.mostrarErrores(validacion)
      return
    }
    this.limpiarErrores()
    this.mostrarLoading()
  }

  // ---- Validación ----

  // Detecta el modo del form: si hay acordeones por tipo es Pantalla 1
  // (subida con titulares); si no, asume Pantalla 3 (firma LOPD: un solo
  // archivo). El modo se infiere del DOM en vez de pedir un value para
  // que las pantallas que reusen este controller no tengan que cablear
  // configuración redundante.
  esModoArchivoUnico () {
    return this.elementoRaiz().querySelectorAll('.rnk-HipotecasDoc_AccItem').length === 0
  }

  validarFormulario () {
    if (this.esModoArchivoUnico()) return this.validarArchivoUnico()

    const numTitulares = this.numTitularesActuales()
    const missingByTitular = {}
    let hasErrors = false
    for (let t = 1; t <= numTitulares; t += 1) {
      const faltan = this.tiposObligatoriosValue.filter(tipo => !this.titularTieneTipo(t, tipo))
      if (faltan.length > 0) {
        missingByTitular[t] = faltan
        hasErrors = true
      }
    }
    const consentOk = this.consentimientoMarcado()
    if (!consentOk) hasErrors = true
    return { hasErrors, missingByTitular, consentOk, numTitulares, modo: 'titulares' }
  }

  validarArchivoUnico () {
    const input = this.element.querySelector('input[type="file"]')
    const tieneArchivo = Boolean(input && input.files && input.files.length > 0)
    return { hasErrors: !tieneArchivo, archivoFaltante: !tieneArchivo, modo: 'archivo-unico' }
  }

  titularTieneTipo (titular, tipo) {
    const root = this.elementoRaiz()
    const item = root.querySelector(`.rnk-HipotecasDoc_AccItem[data-tipo="${tipo}"]`)
    if (!item) return false
    const input = item.querySelector(`[data-titular="${titular}"] input[type="file"]`)
    return Boolean(input && input.files && input.files.length > 0)
  }

  consentimientoMarcado () {
    if (this.hasConsentimientoTarget) return this.consentimientoTarget.checked
    const cb = this.element.querySelector('input[name="consentimiento_cesion"]')
    // Sin checkbox en el DOM no hay consentimiento que validar: el canal
    // tramitación no lo pinta (TC-124-2.2.11.25, envío a bancos = art. 6.1.b).
    if (!cb) return true
    return cb.checked
  }

  numTitularesActuales () {
    const root = this.elementoRaiz()
    const contador = root.querySelector('[data-hipotecas-doc-titulares-target="contador"]')
    if (!contador) return 1
    const n = parseInt(contador.textContent, 10)
    return Number.isFinite(n) && n >= 1 ? n : 1
  }

  elementoRaiz () {
    return this.element.closest('.rnk-HipotecasDoc') || document
  }

  // ---- Pintado de errores ----

  mostrarErrores (validacion) {
    if (validacion.modo === 'archivo-unico') return this.mostrarErrorArchivoUnico()
    const { missingByTitular, consentOk, numTitulares } = validacion
    this.errorsByTitular = Object.assign({}, missingByTitular)
    this.pintarBanner({ missingByTitular, consentOk, numTitulares })
    const titularesFaltantes = Object.keys(missingByTitular).map(Number).sort((a, b) => a - b)
    if (titularesFaltantes.length > 0) {
      const firstErrorTab = titularesFaltantes[0]
      const currentTab = this.tabActivo()
      if (firstErrorTab !== currentTab && numTitulares > 1) {
        // switchTab dispara tab-changed → handleTabChange re-pinta errores
        this.cambiarATitular(firstErrorTab)
      } else {
        this.reaplicarErroresTabActivo()
      }
      this.abrirAcordeonesFaltantes(this.tabActivo())
    }
    if (this.hasBannerTarget) this.bannerTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  pintarBanner ({ missingByTitular, consentOk, numTitulares }) {
    if (!this.hasBannerTarget) return
    const titularesFaltantes = Object.keys(missingByTitular).map(Number).sort((a, b) => a - b)
    const labels = this.tiposLabelsValue || {}
    const items = []
    titularesFaltantes.forEach(t => {
      const docsLeg = missingByTitular[t].map(f => labels[f] || f).join(', ')
      const prefijo = numTitulares > 1 ? this.titularPrefijoValue.replace('%{n}', String(t)) : ''
      items.push(`<li>${this.escape(prefijo)}${this.escape(docsLeg)}</li>`)
    })
    if (!consentOk) items.push(`<li>${this.escape(this.consentimientoFaltanteValue)}</li>`)
    const html = `<strong>${this.escape(this.bannerTituloValue)}</strong><ul>${items.join('')}</ul>`
    this.bannerTarget.innerHTML = html
    this.bannerTarget.hidden = false
  }

  reaplicarErroresTabActivo () {
    const tab = this.tabActivo()
    const root = this.elementoRaiz()
    // Primero limpiamos cualquier error visual residual de este tab
    root.querySelectorAll('.rnk-HipotecasDoc_AccItem.rnk-HipotecasDoc_AccItem-error').forEach(item => {
      item.classList.remove('rnk-HipotecasDoc_AccItem-error')
    })
    root.querySelectorAll('.rnk-HipotecasDoc_AccErrorMsg').forEach(msg => msg.remove())
    const missing = this.errorsByTitular[tab]
    if (!missing || missing.length === 0) return
    missing.forEach(tipo => {
      const item = root.querySelector(`.rnk-HipotecasDoc_AccItem[data-tipo="${tipo}"]`)
      if (!item) return
      if (this.titularTieneTipo(tab, tipo)) return
      item.classList.add('rnk-HipotecasDoc_AccItem-error')
      const msg = document.createElement('div')
      msg.className = 'rnk-HipotecasDoc_AccErrorMsg'
      msg.textContent = this.documentoFaltanteValue
      item.appendChild(msg)
    })
  }

  // Pantalla 3 (firma LOPD): banner con un solo `<li>` + dropzone en rojo.
  // No hay acordeones por titular ni checkbox de consent, así que esto es
  // mucho más simple que el flujo de Pantalla 1.
  mostrarErrorArchivoUnico () {
    if (this.hasBannerTarget) {
      const html = `<strong>${this.escape(this.bannerTituloValue)}</strong><ul><li>${this.escape(this.archivoFaltanteValue)}</li></ul>`
      this.bannerTarget.innerHTML = html
      this.bannerTarget.hidden = false
      this.bannerTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    const dropzoneEl = this.element.querySelector('[data-controller~="hipotecas-doc-dropzone"]')
    if (dropzoneEl) dropzoneEl.classList.add('rnk-HipotecasDoc_Dropzone-error')
  }

  abrirAcordeonesFaltantes (titular) {
    const root = this.elementoRaiz()
    this.tiposObligatoriosValue.forEach(tipo => {
      if (this.titularTieneTipo(titular, tipo)) return
      const item = root.querySelector(`.rnk-HipotecasDoc_AccItem[data-tipo="${tipo}"]`)
      const header = item && item.querySelector('.rnk-HipotecasDoc_AccHeader')
      const body = item && item.querySelector('.rnk-HipotecasDoc_AccBody')
      if (header && !header.classList.contains('rnk-HipotecasDoc_AccHeader-open')) {
        header.classList.add('rnk-HipotecasDoc_AccHeader-open')
      }
      if (body && !body.classList.contains('rnk-HipotecasDoc_AccBody-open')) {
        body.classList.add('rnk-HipotecasDoc_AccBody-open')
      }
    })
  }

  tabActivo () {
    const root = this.elementoRaiz()
    const stepperEl = root.matches('[data-controller~="hipotecas-doc-titulares"]')
      ? root
      : root.querySelector('[data-controller~="hipotecas-doc-titulares"]')
    if (!stepperEl) return 1
    const ctl = this.application.getControllerForElementAndIdentifier(stepperEl, 'hipotecas-doc-titulares')
    return ctl && ctl.currentTab ? ctl.currentTab : 1
  }

  cambiarATitular (titular) {
    const root = this.elementoRaiz()
    const stepperEl = root.matches('[data-controller~="hipotecas-doc-titulares"]')
      ? root
      : root.querySelector('[data-controller~="hipotecas-doc-titulares"]')
    if (!stepperEl) return
    const ctl = this.application.getControllerForElementAndIdentifier(stepperEl, 'hipotecas-doc-titulares')
    if (ctl) ctl.cambiarA(titular)
  }

  handleTabChange () {
    this.reaplicarErroresTabActivo()
  }

  // Cuando el usuario sube un archivo en un acordeón con error del titular
  // activo, limpiamos el error inline (item-error + msg) y lo borramos del
  // estado persistente para que un futuro tab-changed no lo re-añada.
  // Si se queda con consent OK y missing vacío, ocultamos el banner.
  handleFileChange (event) {
    if (!event.target.matches || !event.target.matches('input[type="file"]')) return
    const hasFiles = event.target.files && event.target.files.length > 0
    if (!hasFiles) return

    // Modo Pantalla 3 (firma LOPD): un solo input, sin acordeones. Si había
    // banner + dropzone en rojo por validación previa, los quitamos en
    // cuanto el usuario suba el archivo.
    if (this.esModoArchivoUnico()) {
      if (this.hasBannerTarget) this.bannerTarget.hidden = true
      this.element.querySelectorAll('.rnk-HipotecasDoc_Dropzone-error').forEach(el => el.classList.remove('rnk-HipotecasDoc_Dropzone-error'))
      return
    }

    const bloque = event.target.closest('[data-titular]')
    const item = event.target.closest('.rnk-HipotecasDoc_AccItem')
    if (!item) return
    const tipo = item.dataset.tipo
    const titular = bloque ? parseInt(bloque.dataset.titular, 10) : null
    if (!tipo) return
    if (titular && this.errorsByTitular[titular]) {
      this.errorsByTitular[titular] = this.errorsByTitular[titular].filter(t => t !== tipo)
      if (this.errorsByTitular[titular].length === 0) delete this.errorsByTitular[titular]
    }
    // Limpiar visual del item activo
    if (item.classList.contains('rnk-HipotecasDoc_AccItem-error')) {
      item.classList.remove('rnk-HipotecasDoc_AccItem-error')
      item.querySelectorAll('.rnk-HipotecasDoc_AccErrorMsg').forEach(m => m.remove())
    }
    // Re-pintar el banner si todavía quedan errores; ocultar si ya no
    const numTitulares = this.numTitularesActuales()
    const stillMissing = Object.keys(this.errorsByTitular).length > 0
    const consentOk = this.consentimientoMarcado()
    if (!stillMissing && consentOk) {
      if (this.hasBannerTarget) this.bannerTarget.hidden = true
    } else if (this.hasBannerTarget && !this.bannerTarget.hidden) {
      this.pintarBanner({ missingByTitular: this.errorsByTitular, consentOk, numTitulares })
    }
  }

  limpiarErrores () {
    if (this.hasBannerTarget) {
      this.bannerTarget.hidden = true
      this.bannerTarget.innerHTML = ''
    }
    const root = this.elementoRaiz()
    root.querySelectorAll('.rnk-HipotecasDoc_AccItem-error').forEach(item => item.classList.remove('rnk-HipotecasDoc_AccItem-error'))
    root.querySelectorAll('.rnk-HipotecasDoc_AccErrorMsg').forEach(m => m.remove())
    root.querySelectorAll('.rnk-HipotecasDoc_Dropzone-error').forEach(el => el.classList.remove('rnk-HipotecasDoc_Dropzone-error'))
    this.errorsByTitular = {}
  }

  mostrarLoading () {
    if (this.hasBotonTarget) {
      this.botonTarget.disabled = true
      if (this.loadingLabelValue) {
        this.botonTarget.value = this.loadingLabelValue
      }
      this.botonTarget.classList.add('rnk-HipotecasDoc_Submit-loading')
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = false
    }
  }

  // Escape HTML para que las labels (que pueden tener < o &) no inyecten markup.
  escape (str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}
